/**
 * Translation Utilities
 * Recent languages localStorage management
 */

const RECENT_LANGUAGES_KEY = 'recent_translation_languages';
const MAX_RECENT_LANGUAGES = 3;

export interface RecentLanguage {
  code: string;
  name: string;
  nativeName: string;
  countryCode: string;
  lastUsed: string;
}

/**
 * Get recent languages from localStorage
 */
export function getRecentLanguages(): RecentLanguage[] {
  try {
    const stored = localStorage.getItem(RECENT_LANGUAGES_KEY);
    if (!stored) return [];
    
    const languages = JSON.parse(stored) as RecentLanguage[];
    
    // Sort by lastUsed (most recent first)
    return languages.sort((a, b) => 
      new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime()
    );
  } catch (error) {
    console.error('Failed to get recent languages:', error);
    return [];
  }
}

/**
 * Add a language to recent languages
 */
export function addRecentLanguage(language: {
  code: string;
  name: string;
  nativeName: string;
  countryCode: string;
}): void {
  try {
    const recent = getRecentLanguages();
    
    // Remove if already exists
    const filtered = recent.filter(l => l.code !== language.code);
    
    // Add to beginning with current timestamp
    const updated: RecentLanguage[] = [
      {
        ...language,
        lastUsed: new Date().toISOString()
      },
      ...filtered
    ];
    
    // Keep only MAX_RECENT_LANGUAGES
    const trimmed = updated.slice(0, MAX_RECENT_LANGUAGES);
    
    localStorage.setItem(RECENT_LANGUAGES_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error('Failed to add recent language:', error);
  }
}

/**
 * Clear recent languages
 */
export function clearRecentLanguages(): void {
  try {
    localStorage.removeItem(RECENT_LANGUAGES_KEY);
  } catch (error) {
    console.error('Failed to clear recent languages:', error);
  }
}

/**
 * Get default target language (most recent or fallback to English)
 */
export function getDefaultTargetLanguage(): string {
  const recent = getRecentLanguages();
  return recent.length > 0 ? recent[0].code : 'en';
}
